import { type FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCompleteContractMutation } from '@api/queries/contract';
import { useHorizontalScroll } from '@common/hooks/horizontal-hook';
import { ContractsLock } from '@components/ContractsLock/ContractsLock';
import { IntroContext } from '@components/Intro/context.ts';
import { useAppSelector } from '@redux/store';
import cn from 'classnames';
import { AnimatePresence } from 'framer-motion';
import mergeRefs from 'merge-refs';

import { useGetContracts } from '@/common/hooks/request-for-get-contracts';
import { checkIfLocakStorageDateIsToday } from '@/common/lib/common';
import { type MainPageContractTabs } from '@/common/types/contract';
import { CompleteContractAnimationContext } from '@/components/Animations/CompleteContractAnimation/context';
import { StringXSmall } from '@/components/Typography/Typography';
import {
  selectVisibleProductsOfDayToClaim,
  selectVisibleSpecialContractsCount,
} from '@/redux/selectors/contractSelectors';

import { ContractProgress } from '../ContractProgress/ContractProgress';

import { ContractTimersHandler } from './ContractTimersHandler/ContractTimersHandler';
import ContractList from './tabs/ContractsList';
import DailyContracts from './tabs/DailyContracts';
import SpecialContractsList from './tabs/SpecialContractsList';
import { UpdateDailyContractsTimer } from './UpdateContractTimer/UpdateDailyContractsTimer';
import { UpdateSpecContractsTimer } from './UpdateContractTimer/UpdateSpecContractsTimer';

import css from './styles.module.scss';

const Contracts: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dailyContractsIsVisit, setDailyContractsIsVisit] = useState(
    checkIfLocakStorageDateIsToday('last-daily-contracts-visit'),
  );
  const { activeStep } = useContext(IntroContext);
  const initialTabParam = searchParams.get('initialTab');
  const isValidTab = (value: string | null): value is MainPageContractTabs =>
    value === 'standart' || value === 'daily' || value === 'special';
  const [tab, setTab] = useState<MainPageContractTabs>(
    isValidTab(initialTabParam) ? initialTabParam : 'standart',
  );

  useEffect(() => {
    if (initialTabParam !== null) {
      const next = new URLSearchParams(searchParams);
      next.delete('initialTab');
      setSearchParams(next, { replace: true });
    }
  }, []);
  const [onlyActive] = useState(false);
  const minLevelForContracts = useAppSelector(
    (state) => state.user.user?.settings.minUserLevelForContracts,
  );
  const { contractListWrapperRef } = useContext(
    CompleteContractAnimationContext,
  );
  const userPoints = useAppSelector((state) => state.contract.userPoints);
  const userLevel = useAppSelector((state) => state.user.user?.level);
  const ref = useHorizontalScroll<HTMLDivElement>(undefined, activeStep !== 23);
  const [, { isUninitialized }] = useCompleteContractMutation({
    fixedCacheKey: 'shared-complete-contract',
  });

  const visibleSpecialContractsCount = useAppSelector((state) =>
    selectVisibleSpecialContractsCount(state, userLevel || 0),
  );
  const productsOfDayToClaimCount = useAppSelector((state) =>
    selectVisibleProductsOfDayToClaim(state),
  ).length;

  const getContracts = useGetContracts();

  useEffect(() => {
    getContracts();
  }, [minLevelForContracts, userLevel]);

  if (userLevel === undefined || minLevelForContracts === undefined) {
    return null;
  }

  if (userLevel < minLevelForContracts) {
    return <ContractsLock minLevel={minLevelForContracts} />;
  }

  return (
    <article className="flex flex-col flex-1 justify-center">
      <ContractTimersHandler />
      <div className="contract-title_all-points min-h-6">
        <div className="flex !gap-4">
          <button
            className={cn(
              'font-black text-base leading-none',
              tab === 'standart' && 'text-accent',
            )}
            onClick={() => setTab('standart')}
          >
            {t('Standart')}
          </button>
          <button
            className={cn(
              'font-black text-base leading-none flex gap-0.5 relative',
              tab === 'daily' && 'text-accent',
            )}
            onClick={() => setTab('daily')}
          >
            {t('Daily')}
            {productsOfDayToClaimCount > 0 && (
              <div className="bg-accent rounded-full size-4 flex items-center justify-center">
                <StringXSmall className="!text-main-bg !font-bold">
                  {productsOfDayToClaimCount}
                </StringXSmall>
              </div>
            )}
            {!dailyContractsIsVisit && (
              <div className="activeGreenPoint -top-[22%] -right-[20%]"></div>
            )}
          </button>
          <button
            className={cn(
              'font-black text-base leading-none flex gap-0.5',
              tab === 'special' && 'text-accent',
            )}
            onClick={() => setTab('special')}
          >
            {t('Special')}
            {visibleSpecialContractsCount > 0 && (
              <div className="bg-accent rounded-full size-4 flex items-center justify-center">
                <StringXSmall className="!text-main-bg !font-bold">
                  {visibleSpecialContractsCount}
                </StringXSmall>
              </div>
            )}
          </button>
        </div>
        {tab === 'daily' && <UpdateDailyContractsTimer />}
        {tab === 'special' && <UpdateSpecContractsTimer />}
      </div>
      <div
        ref={mergeRefs(ref, contractListWrapperRef)}
        className={cn(
          css.contractList,
          activeStep === 23 ? 'overflow-hidden' : 'overflow-x-auto',
        )}
      >
        <AnimatePresence>
          {tab === 'standart' && (
            <ContractList
              isUninitialized={isUninitialized}
              userLevel={userLevel}
            />
          )}
          {tab === 'daily' && (
            <DailyContracts
              setDailyContractsIsVisit={setDailyContractsIsVisit}
              isUninitialized={isUninitialized}
            />
          )}
          {tab === 'special' && (
            <SpecialContractsList
              isUninitialized={isUninitialized}
              userLevel={userLevel}
              onlyActive={onlyActive}
            />
          )}
        </AnimatePresence>
      </div>
      {userPoints !== null && <ContractProgress />}
    </article>
  );
};

export { Contracts };
